import { PageController } from '@client/components/_core/layout/PageController';
import { Loading } from '@client/components/_ui/feedback/Loading';
import { ComplaintWorkloadList } from '@client/components/complaints/complaints-workload/ComplaintWorkloadList';
import { OwnerTimelineLane } from '@client/components/complaints/complaints-workload/OwnerTimelineLane';
import { useTrackEngagementView } from '@client/hooks/business/engagement/useTrackEngagementView';
import { useUser } from '@client/hooks/business/user/useUser';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { useFeatureFlag, useFeatureFlagSettlement } from '@client/hooks/useFeatureFlag';
import { selectOrg, useAuthStore } from '@client/stores/AuthStore';
import { trpc } from '@client/trpc/client';
import { COMPLAINTS_WORKLOAD_FLAG } from '@shared/constants/business/complaint';
import { createFileRoute } from '@tanstack/react-router';
import { type Key, useEffect, useRef, useState } from 'react';

export const Route = createFileRoute('/console/$orgId/workload-manager/complaints')({
    staticData: {
        metadata: {
            title: 'Complaints',
        },
    },
    component: ComplaintsWorkloadRouteGuard,
});

/**
 * Decided in render rather than in `beforeLoad` (CUR-2685): `beforeLoad` runs before `RequireAuth`
 * has moved the session into the URL's organisation, so a deep link from a session still holding
 * another org (or none yet) was judged by that org's flags, and `getFlag` could answer `false` on
 * a cold load before the flags were evaluated for this identity. Wait until the auth store holds
 * the URL's org (on an in-app switch this guard's effects run before `RequireAuth` has swapped it);
 * the page then renders once `enable-complaints-workload` has been evaluated on for this user in
 * the URL's org, and the user is sent to Tasks once it has been evaluated off. A value read before
 * that evaluation is not trusted: after an org switch PostHog keeps the previous organisation's map
 * until the reload lands, so a `true` may be that org's and the workload's requests would answer
 * `NOT_FOUND`. A timed-out evaluation also redirects, whatever that retained value says, as the
 * `getFlag` default did: the page has no URL state to lose and the Complaints tab comes back when
 * the flag arrives.
 */
function ComplaintsWorkloadRouteGuard() {
    const { orgId } = Route.useParams();
    const isUrlOrg = useAuthStore((state) => selectOrg(state)?.id === orgId);
    const navigate = useNavigate();
    const user = useUser({ throwOnUndefined: false });
    const settlement = useFeatureFlagSettlement({ userId: user?.id.toString(), orgId: orgId.toString() });
    const workloadEnabled = useFeatureFlag(COMPLAINTS_WORKLOAD_FLAG, false);
    const isEvaluated = isUrlOrg && settlement === 'evaluated';
    const shouldRedirect = isUrlOrg && (settlement === 'timed-out' || (isEvaluated && !workloadEnabled));

    const navigateRef = useRef(navigate);
    navigateRef.current = navigate;
    useEffect(() => {
        if (shouldRedirect) {
            navigateRef.current({ to: '/console/$orgId/workload-manager/tasks', params: { orgId }, replace: true });
        }
    }, [shouldRedirect, orgId]);

    if (isEvaluated && workloadEnabled) {
        return <WorkloadManagerComplaints />;
    }
    return shouldRedirect ? null : <Loading />;
}

function WorkloadManagerComplaints() {
    useTrackEngagementView('workload_tab_viewed', { tab: 'complaints' });
    const { data: ownersData, isLoading: ownersLoading } = trpc.complaint.getComplaintOwnerSignals.useQuery();

    const [expanded, setExpanded] = useState<Set<string>>(new Set());
    const [selection, setSelection] = useState<Record<string, Key[]>>({});

    function toggleExpanded(ownerId: string) {
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(ownerId)) next.delete(ownerId);
            else next.add(ownerId);
            return next;
        });
    }

    function setOwnerSelection(ownerId: string, keys: Key[]) {
        setSelection((prev) => ({ ...prev, [ownerId]: keys }));
    }

    // Dot click opens the lane (idempotent) and toggles the clicked complaint IDs
    // in the owner's selection. Never closes the lane.
    function handleDotClick(ownerId: string, ids: string[]) {
        setExpanded((prev) => {
            if (prev.has(ownerId)) return prev;
            const next = new Set(prev);
            next.add(ownerId);
            return next;
        });
        setSelection((prev) => {
            const current = prev[ownerId] ?? [];
            const currentSet = new Set(current.map(String));
            const allAlreadySelected = ids.every((id) => currentSet.has(id));
            const nextKeys: Key[] = allAlreadySelected
                ? current.filter((k) => !ids.includes(String(k)))
                : Array.from(new Set([...current.map(String), ...ids]));
            return { ...prev, [ownerId]: nextKeys };
        });
    }

    return (
        <PageController route={Route}>
            {ownersLoading ? (
                <Loading />
            ) : !ownersData || ownersData.length === 0 ? (
                <div className="p-6 text-muted-foreground">No complaint owners yet</div>
            ) : (
                <div className="flex flex-col divide-y divide-separator-secondary rounded-lg border border-separator bg-card">
                    {ownersData.map((row, i) => {
                        const isOpen = expanded.has(row.ownerId);
                        return (
                            <div key={row.ownerId}>
                                <OwnerTimelineLane
                                    owner={{ id: row.ownerId, name: row.ownerName, initials: row.ownerInitials }}
                                    signals={{
                                        pressureBucket: row.pressureBucket,
                                        openCount: row.openCount,
                                    }}
                                    ownComplaints={row.complaints}
                                    selected={isOpen}
                                    onSelect={() => toggleExpanded(row.ownerId)}
                                    onDotClick={(ids) => handleDotClick(row.ownerId, ids)}
                                    showAxis={i === 0}
                                />
                                {isOpen && (
                                    <div className="bg-muted/30 px-3 py-3" onClick={(e) => e.stopPropagation()}>
                                        <ComplaintWorkloadList
                                            filters={{ ownerId: row.ownerId }}
                                            rowSelection={{
                                                selectedRowKeys: selection[row.ownerId] ?? [],
                                                setSelectedRowKeys: (updater) => {
                                                    const current = selection[row.ownerId] ?? [];
                                                    const next =
                                                        typeof updater === 'function' ? updater(current) : updater;
                                                    setOwnerSelection(row.ownerId, next);
                                                },
                                            }}
                                            hideOwnerColumn
                                        />
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </PageController>
    );
}
